/**
 * formatRelativeChatTime — shared "X ago" formatter for chat thread rows.
 *
 * Lifted from ``components/chat/ChatHistory.tsx`` (Stream 12) when the
 * Tracker sidebar (W4 of the chat-wireup brief) became the second
 * surface that needed to render relative timestamps for thread rows.
 * Two callers is the threshold the original ChatHistory comment
 * deferred to — once a third surface exists, this is the single source
 * of truth.
 *
 * Output ladder (mirrors the original implementation exactly):
 *   - empty / unparseable input → ``""``
 *   - < 60s   → ``"just now"``
 *   - < 60m   → ``"{n}m ago"``
 *   - < 24h   → ``"{n}h ago"``
 *   - < 30d   → ``"{n}d ago"``
 *   - else    → ``new Date(iso).toLocaleDateString()``
 *
 * Note on timezone: ``toLocaleDateString`` is locale + timezone aware
 * (uses the user's browser settings). For chat history the precision
 * is "month + day" which is robust to TZ off-by-one for any user
 * actually online in the relevant week, so we don't pin a TZ here.
 */
export const formatRelativeChatTime = (iso: string): string => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default formatRelativeChatTime;
